/**
 * Task Management Connector configuration component.
 * Specialized configuration for Task Management connector (JIRA / Azure DevOps).
 */

// React Imports
import { useState } from 'react';
import { Input } from '@coordio/ui';

export interface TaskManagementConnectorConfigProps {
  settingsJson?: string;
  onChange?: (settingsJson: string) => void;
}

/**
 * Task Management Connector configuration component.
 */
export const TaskManagementConnectorConfig = ({
  settingsJson = '{}',
  onChange,
}: TaskManagementConnectorConfigProps) => {
  const [provider, setProvider] = useState<'jira' | 'azuredevops'>('jira');
  const [defaultProject, setDefaultProject] = useState('');
  const [defaultIssueType, setDefaultIssueType] = useState('Task');

  const handleProviderChange = (newProvider: 'jira' | 'azuredevops') => {
    setProvider(newProvider);
    const settings = JSON.parse(settingsJson || '{}');
    settings.provider = newProvider;
    onChange?.(JSON.stringify(settings));
  };

  return (
    <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
      <div className="p-6">
        <h6 className="text-lg font-semibold tracking-tight mb-6">
          Task Management Provider Configuration
        </h6>

        <div className="mb-6">
          <p className="text-sm text-muted-foreground mb-2">
            Select Provider
          </p>
          <div className="inline-flex w-full rounded-md border" role="group">
            <button
              type="button"
              className={`flex-1 px-4 py-2 text-sm font-medium border-r transition-colors rounded-l-md ${
                provider === 'jira' ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted'
              }`}
              onClick={() => handleProviderChange('jira')}
            >
              JIRA
            </button>
            <button
              type="button"
              className={`flex-1 px-4 py-2 text-sm font-medium transition-colors rounded-r-md ${
                provider === 'azuredevops' ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted'
              }`}
              onClick={() => handleProviderChange('azuredevops')}
            >
              Azure DevOps
            </button>
          </div>
        </div>

        {provider === 'jira' && (
          <div className="flex flex-col gap-4">
            <div className="space-y-1">
              <label className="text-sm font-medium">Default Project Key</label>
              <Input
                type="text"
                className="w-full"
                value={defaultProject}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setDefaultProject(e.target.value);
                  const settings = JSON.parse(settingsJson || '{}');
                  settings.defaultProject = e.target.value;
                  onChange?.(JSON.stringify(settings));
                }}
              />
              <p className="text-xs text-muted-foreground">Default JIRA project key (e.g., PROJ)</p>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Default Issue Type</label>
              <Input
                type="text"
                className="w-full"
                value={defaultIssueType}
                onChange={(e) => {
                  setDefaultIssueType(e.target.value);
                  const settings = JSON.parse(settingsJson || '{}');
                  settings.defaultIssueType = e.target.value;
                  onChange?.(JSON.stringify(settings));
                }}
              />
              <p className="text-xs text-muted-foreground">Default issue type (e.g., Task, Bug, Story)</p>
            </div>
            <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800 dark:border-blue-800/40 dark:bg-blue-950/50 dark:text-blue-300">
              JIRA supports both Cloud (OAuth) and Server (API token) authentication. Configure authentication in the Authentication step.
            </div>
          </div>
        )}

        {provider === 'azuredevops' && (
          <div className="flex flex-col gap-4">
            <div className="space-y-1">
              <label className="text-sm font-medium">Default Project Name</label>
              <Input
                type="text"
                className="w-full"
                value={defaultProject}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setDefaultProject(e.target.value);
                  const settings = JSON.parse(settingsJson || '{}');
                  settings.defaultProject = e.target.value;
                  onChange?.(JSON.stringify(settings));
                }}
              />
              <p className="text-xs text-muted-foreground">Default Azure DevOps project name</p>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Default Work Item Type</label>
              <Input
                type="text"
                className="w-full"
                value={defaultIssueType}
                onChange={(e) => {
                  setDefaultIssueType(e.target.value);
                  const settings = JSON.parse(settingsJson || '{}');
                  settings.defaultWorkItemType = e.target.value;
                  onChange?.(JSON.stringify(settings));
                }}
              />
              <p className="text-xs text-muted-foreground">Default work item type (e.g., Task, Bug, User Story)</p>
            </div>
            <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800 dark:border-blue-800/40 dark:bg-blue-950/50 dark:text-blue-300">
              Azure DevOps supports Personal Access Token (PAT) or OAuth authentication. Configure in the Authentication step.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
